import React from "react";
import type { TFunction } from "i18next";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { ConnectionStatus } from "../../../services/socket";

type TranscriptionStatus = "idle" | "started" | "error" | "stopped";

type StatusTone = "ok" | "warn" | "bad" | "neutral";

const TONE_COLOR: Record<StatusTone, { dot: string; glow: string; text: string }> = {
  ok: { dot: "#22c55e", glow: "rgba(34, 197, 94, 0.6)", text: "#22c55e" },
  warn: { dot: "#f97316", glow: "rgba(249, 115, 22, 0.6)", text: "#f97316" },
  bad: { dot: "#ef4444", glow: "rgba(239, 68, 68, 0.6)", text: "#ef4444" },
  neutral: { dot: "#94a3b8", glow: "rgba(148, 163, 184, 0.5)", text: "#94a3b8" },
};

const CONNECTION_TONE: Record<ConnectionStatus, StatusTone> = {
  online: "ok",
  degraded: "warn",
  reconnecting: "warn",
  offline: "bad",
};

const TRANSCRIPTION_TONE: Record<TranscriptionStatus, StatusTone> = {
  idle: "neutral",
  started: "ok",
  error: "bad",
  stopped: "neutral",
};

const PULSE_STATES = new Set<string>(["reconnecting", "degraded", "error"]);

interface StatusIconProps {
  testId: string;
  tone: StatusTone;
  icon: string;
  pulse?: boolean;
  badge?: React.ReactNode;
  tooltip: React.ReactNode;
}

function StatusIcon({ testId, tone, icon, pulse, badge, tooltip }: StatusIconProps) {
  const color = TONE_COLOR[tone];
  return (
    <Tooltip>
      <TooltipTrigger
        render={(triggerProps) => (
          <div
            {...triggerProps}
            data-testid={testId}
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              cursor: "default",
              animation: pulse ? "pulse 1.5s infinite" : "none",
            }}
          >
            <i
              className={`bx ${icon}`}
              style={{
                fontSize: "1.25rem",
                color: color.text,
                filter: `drop-shadow(0 0 4px ${color.glow})`,
              }}
            />
            {badge}
          </div>
        )}
      />
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

interface StatusIndicatorsProps {
  connectionStatus: ConnectionStatus;
  transcriptionStatus: TranscriptionStatus;
  pendingLogsCount: number;
  t: TFunction;
}

export function StatusIndicators({
  connectionStatus,
  transcriptionStatus,
  pendingLogsCount,
  t,
}: StatusIndicatorsProps) {
  const connLabel = t(`logger.layout.${connectionStatus}`);
  const connTip = t(
    {
      online: "logger.layout.connectionOnlineTip",
      degraded: "logger.layout.connectionDegradedTip",
      reconnecting: "logger.layout.connectionReconnectingTip",
      offline: "logger.layout.connectionOfflineTip",
    }[connectionStatus],
  );

  const txKey = {
    idle: "logger.layout.transcriptionIdle",
    started: "logger.layout.transcriptionActive",
    error: "logger.layout.transcriptionError",
    stopped: "logger.layout.transcriptionStopped",
  }[transcriptionStatus];
  const txTipKey = {
    idle: "logger.layout.transcriptionIdleTip",
    started: "logger.layout.transcriptionActiveTip",
    error: "logger.layout.transcriptionErrorTip",
    stopped: "logger.layout.transcriptionStoppedTip",
  }[transcriptionStatus];

  const connIcon =
    connectionStatus === "offline"
      ? "bx-wifi-off"
      : connectionStatus === "online"
        ? "bx-wifi"
        : "bx-wifi-2";

  const txIcon =
    transcriptionStatus === "started"
      ? "bx-microphone"
      : transcriptionStatus === "error"
        ? "bx-microphone-off"
        : "bx-microphone-off";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
      <StatusIcon
        testId="logger-connection-status"
        tone={CONNECTION_TONE[connectionStatus]}
        icon={connIcon}
        pulse={PULSE_STATES.has(connectionStatus)}
        tooltip={
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold">{connLabel}</span>
            <span className="text-xs opacity-80">{connTip}</span>
            {pendingLogsCount > 0 && (
              <span className="text-xs" style={{ color: "#eab308" }}>
                {pendingLogsCount} pending
              </span>
            )}
          </div>
        }
        badge={
          pendingLogsCount > 0 ? (
            <span
              style={{
                position: "absolute",
                top: "-2px",
                right: "-2px",
                minWidth: "14px",
                height: "14px",
                padding: "0 3px",
                borderRadius: "7px",
                background: "#eab308",
                color: "#000",
                fontSize: "0.55rem",
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1,
              }}
            >
              {pendingLogsCount}
            </span>
          ) : undefined
        }
      />
      <StatusIcon
        testId="logger-transcription-status"
        tone={TRANSCRIPTION_TONE[transcriptionStatus]}
        icon={txIcon}
        pulse={transcriptionStatus === "error"}
        tooltip={
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold">
              {t("logger.layout.transcription")} · {t(txKey)}
            </span>
            <span className="text-xs opacity-80">{t(txTipKey)}</span>
          </div>
        }
      />
    </div>
  );
}
